import { useState, useEffect } from "react";
import { Plus, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  getSharedSavedReplies,
  createSharedSavedReply,
  updateSharedSavedReply,
  deleteSharedSavedReply,
  readApiError,
} from "./supportApi";
import SupportConfirmationModal from "./SupportConfirmationModal";

// The backend's Language enum has exactly these two.
const LANGUAGES = [
  { id: "en", name: "English" },
  { id: "fa", name: "Persian" },
];

const NAME_MAX = 100;
const CONTENT_MAX = 5000;

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US");
};

export default function SavedReplies() {
  const [replies, setReplies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [languageFilter, setLanguageFilter] = useState("all");

  const [showForm, setShowForm] = useState(false);
  const [editingReply, setEditingReply] = useState(null);
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en");
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, reply: null });
  const [deletingId, setDeletingId] = useState(null);

  // These endpoints answer with { error } only — no `message` — unlike the article
  // routes, so read both rather than showing an empty toast.

  const fetchReplies = async (lang = languageFilter) => {
    setIsLoading(true);
    try {
      const data = await getSharedSavedReplies(lang);
      setReplies(data.savedReplies || []);
    } catch (error) {
      console.error("Error fetching saved replies:", error);
      toast.error("Failed to load saved replies");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReplies(languageFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageFilter]);

  const resetForm = () => {
    setEditingReply(null);
    setName("");
    setLanguage("en");
    setContent("");
    setShowForm(false);
  };

  const startEdit = (reply) => {
    setEditingReply(reply);
    setName(reply.name);
    setLanguage(reply.language);
    setContent(reply.content);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !content.trim()) {
      toast.error("Name and message are both required");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { name: name.trim(), content: content.trim(), language };
      const response = editingReply
        ? await updateSharedSavedReply(editingReply.id, payload)
        : await createSharedSavedReply(payload);

      if (response.ok) {
        toast.success(editingReply ? "Saved reply updated" : "Saved reply created");
        resetForm();
        await fetchReplies(languageFilter);
        return;
      }
      toast.error(await readApiError(response, "Could not save the reply"));
    } catch (error) {
      console.error("Error saving reply:", error);
      toast.error("Could not save the reply. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const reply = deleteModal.reply;
    if (!reply) return;

    setDeletingId(reply.id);
    try {
      const response = await deleteSharedSavedReply(reply.id);
      if (response.ok) {
        toast.success("Saved reply deleted");
        // The form may still be showing the row that just went away.
        if (editingReply?.id === reply.id) resetForm();
        await fetchReplies(languageFilter);
      } else {
        toast.error(await readApiError(response, "Could not delete the reply"));
      }
    } catch (error) {
      console.error("Error deleting reply:", error);
      toast.error("Could not delete the reply. Please try again.");
    } finally {
      setDeletingId(null);
      setDeleteModal({ isOpen: false, reply: null });
    }
  };

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Saved <span className="text-gradient-ginox">Replies</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          Pre-written responses every agent can insert. Agents also keep their own
          private replies — those are not managed here.
        </p>
      </div>

      <div className="max-w-5xl space-y-5">
        {/* Toolbar */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Language
            </label>
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              className="rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 min-w-[150px]"
            >
              <option value="all">All Languages</option>
              {LANGUAGES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>

          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors flex items-center gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Reply
            </button>
          )}
        </div>

        {/* Create / edit form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-border/70 bg-card p-5 space-y-4"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {editingReply ? "Edit saved reply" : "New saved reply"}
            </p>

            <div className="md:flex md:gap-4 flex flex-col gap-4">
              <div className="flex-1 flex flex-col">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={NAME_MAX}
                  placeholder="Ask for account ID"
                  className="rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                />
              </div>
              <div className="flex flex-col">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 min-w-[150px]"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col">
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Message
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={CONTENT_MAX}
                rows={6}
                placeholder="Could you please send me your account ID?"
                className="rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-2 text-xs leading-relaxed text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 resize-y"
              />
              <span className="mt-1 self-end text-[10px] font-mono tabular-nums text-muted-foreground">
                {content.length} / {CONTENT_MAX}
              </span>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isSubmitting && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                {isSubmitting ? "Saving..." : editingReply ? "Save changes" : "Create"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={isSubmitting}
                className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* List */}
        {isLoading ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
          </div>
        ) : replies.length === 0 ? (
          <div className="flex items-start gap-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <AlertCircle
              className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <p className="text-yellow-400 text-sm">
              No shared replies yet. Add one above and every agent will be able to
              insert it.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
              <thead>
                <tr>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Name
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Language
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Message
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Updated
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {replies.map((reply) => (
                  <tr
                    key={reply.id}
                    className="hover:bg-white/[0.04] transition"
                  >
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                      {reply.name}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground uppercase">
                      {reply.language}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground max-w-md">
                      <span className="line-clamp-2">{reply.content}</span>
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(reply.updatedAt)}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      <div className="flex flex-col justify-center items-center gap-2 text-center">
                        <button
                          onClick={() => startEdit(reply)}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            setDeleteModal({ isOpen: true, reply })
                          }
                          disabled={deletingId === reply.id}
                          className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                            deletingId === reply.id
                              ? "border border-border/40 bg-white/[0.03] text-muted-foreground cursor-not-allowed"
                              : "border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                          }`}
                        >
                          {deletingId === reply.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SupportConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, reply: null })}
        onConfirm={handleDelete}
        title="Delete saved reply"
        message={`Delete "${deleteModal.reply?.name}"? Agents will no longer be able to insert it.`}
        loading={deletingId !== null}
      />
    </div>
  );
}
